import { useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAppState } from 'state';
import styled from 'styled-components';

import useConfig from 'app/config';
import { routes } from 'app/routes/constants';
import urlQueryParams from 'app/utils/requests/query-params';

import LoadingWithOverlay from 'app/ui/components/molecules/loading-with-overlay';
import ErrorComponent from 'app/ui/components/atoms/error';
import HtmlTitle from 'src/app/ui/components/atoms/html-title';

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100vw;
`;

const VerifyOneFiitPage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { config } = useConfig();

  const signedIn = useAppState(({ auth }) => auth.signedIn);
  const authorization = useAppState(({ auth }) => auth.authorization);
  const clientId = searchParams.get('client_id');
  const codeChallenge = searchParams.get('code_challenge');
  const codeChallengeMethod = searchParams.get('code_challenge_method');
  const redirectUri = searchParams.get('redirect_uri');
  const subpath = searchParams.get('subpath');
  const from = searchParams.get('from');

  if (!clientId || !codeChallenge || !codeChallengeMethod || !redirectUri) {
    return (
      <>
        <HtmlTitle title="Verify OneFiit - Error" />
        <Layout>
          <ErrorComponent details="Missing required parameters" />
        </Layout>
      </>
    );
  }

  const redirectToLogin = useCallback(
    (currentUrl: string) => {
      const redirectTo = encodeURIComponent(currentUrl);

      if (config.REACT_APP_SSO_ENABLED) {
        const loginUrl = `${config.REACT_APP_SSO_REDIRECT_URL}?verify_redirect_to=${redirectTo}`;
        window.location.replace(loginUrl);
      } else {
        const signinUrl = `${window.location.origin}${routes.SIGNIN}?verify_redirect_to=${redirectTo}`;
        window.location.replace(signinUrl);
      }
    },
    [config],
  );

  if (!signedIn || !authorization) {
    setTimeout(() => {
      redirectToLogin(`${window.location.origin}${location.pathname}${location.search}`);
    }, 100);
    return (
      <>
        <HtmlTitle title="Verify OneFiit" />
        <Layout>
          <LoadingWithOverlay />
        </Layout>
      </>
    );
  }

  // Construct URL with PKCE parameters to send to REACT_APP_AUTH_CODE_URL
  // This endpoint will exchange the PKCE params for an authorization code and redirect
  const params = urlQueryParams({
    client_id: clientId,
    code_challenge: codeChallenge,
    code_challenge_method: codeChallengeMethod,
    redirect_uri: redirectUri,
    authorization,
    subpath,
    from,
  });

  const authCodeUrl = `${config.REACT_APP_AUTH_CODE_URL}?${params}`;
  window.location.replace(authCodeUrl);

  return (
    <>
      <HtmlTitle title="Verify OneFiit" />
      <Layout>
        <LoadingWithOverlay />
      </Layout>
    </>
  );
};

export default VerifyOneFiitPage;
